type RouteContext = {
    companyId?: string;
    domain?: string;
    params?: Record<string, any>;
    searchParams?: Record<string, any>;
    store_slug?: string;
    promotion_slug?: string;
    pageSlug?: string;
};

const FOOTER_PAGE_SLUGS: Record<string, string> = {
    contact_page: 'contact-us',
    privacy_policy_page: 'privacy-policy',
    terms_of_services_page: 'terms-of-services',
    affiliate_disclosure_page: 'affiliate-disclosure',
    do_not_sell_my_personal_information_page: 'do-not-sell-my-personal-information',
};

const getFooterPageSlug = (pageSlug = ''): string =>
    FOOTER_PAGE_SLUGS[pageSlug] ?? pageSlug.replace(/_page$/, '').replace(/_/g, '-');

const getSlugParts = (params?: Record<string, any>): string[] => {
    const slug = params?.slug;

    if (Array.isArray(slug)) {
        return slug.map((part) => String(part));
    }

    if (typeof slug === 'string' && slug.trim()) {
        return [slug];
    }

    return [];
};

// On /category/[...slug] and /products/[...slug] the route prefix lives in the
// path, so params.slug is just the entity slug. On the root catch-all the prefix
// is the first slug segment instead (/store/aosom.com -> ['store','aosom.com']),
// which would otherwise join into "store/aosom.com" and miss every lookup. The
// store/promotion prefixes are per-company, so they arrive on the context.
const stripRoutePrefix = (slugParts: string[], context?: RouteContext): string[] => {
    if (slugParts.length < 2) return slugParts;

    const prefixes = [context?.store_slug, context?.promotion_slug].filter(Boolean);
    return prefixes.includes(slugParts[0]) ? slugParts.slice(1) : slugParts;
};

export const getRouteTemplateVariables = (context?: RouteContext): Record<string, string> => {
    const rawSlugParts = getSlugParts(context?.params);
    const isAllStoresRoute = rawSlugParts[0] === 'all-stores';
    const alphabet = isAllStoresRoute ? rawSlugParts[1] || 'A' : '';
    const page = isAllStoresRoute && rawSlugParts[2] === 'page' ? rawSlugParts[3] || '1' : '1';

    const slugParts = stripRoutePrefix(rawSlugParts, context);
    const category_slug = slugParts.length > 0 && !isAllStoresRoute ? slugParts.join('/') : '';

    // /search?query=... has no route params at all — the term lives in
    // searchParams, same place `page.tsx` already reads pagination from.
    const rawQuery = context?.searchParams?.query;
    const query = Array.isArray(rawQuery) ? rawQuery[0] || '' : String(rawQuery ?? '');

    // page-content (the reusable About Us/Privacy Policy/etc. block) needs to
    // know which static page it's actually being rendered on — the builder
    // JSON is one artifact shared across every dedicated page route (see
    // About-Us/page.tsx etc.), so it can't bake in a single literal slug the
    // way a Data-tab picker used to. The underlying footer-pages API uses
    // hyphenated slugs ("about-us"), but each page route's own pageSlug is
    // still underscored ("about_us", matching buildPageJson's pageId input).
    const page_slug = getFooterPageSlug(context?.pageSlug);

    return {
        company_id: context?.companyId || '',
        domain: context?.domain || '',
        alphabet,
        page,
        per_page: '33',
        category_slug,
        query,
        page_slug,
    };
};

export const replaceTemplateValue = (value: any, variables: Record<string, string>): any => {
    if (typeof value === 'string') {
        return value.replace(/\{\{(.*?)\}\}/g, (_, rawKey: string) => {
            const key = rawKey.trim();
            return variables[key] ?? '';
        });
    }

    if (Array.isArray(value)) {
        return value.map((entry) => replaceTemplateValue(entry, variables));
    }

    if (value && typeof value === 'object') {
        const replaced: Record<string, any> = {};
        Object.entries(value).forEach(([key, nested]) => {
            replaced[key] = replaceTemplateValue(nested, variables);
        });
        return replaced;
    }

    return value;
};

export const buildRequestConfig = <
    T extends {
        apiEndpoint: string;
        method: 'GET' | 'POST' | 'PUT' | 'DELETE';
        body?: Record<string, any>;
        params?: Record<string, any>;
    }
>(
    config: T | undefined,
    variables: Record<string, string>
): T | undefined => {
    if (!config?.apiEndpoint || !config?.method) {
        return undefined;
    }

    return {
        ...config,
        apiEndpoint: replaceTemplateValue(config.apiEndpoint, variables),
        body: config.body ? replaceTemplateValue(config.body, variables) : undefined,
        params: config.params ? replaceTemplateValue(config.params, variables) : undefined,
    };
};
